import Image           from '@widesk-ui/components/Image';
import View            from '@widesk-ui/components/View';
import Tooltip         from '@widesk-ui/components/Tooltip';
import Spin            from '@widesk-ui/components/Spin';
import useDesignTokens from '@/hooks/useDesignTokens';
import React           from 'react';

const SIZE = 40;

export default function ImageThumbnail(props: {
	src?: string;
	loading?: boolean;
	/** Mis en avant tant qu'un fichier est glissé au-dessus. */
	highlighted?: boolean;
	/** `contain` (défaut) montre l'image entière, comme elle se pose sur une forme ; `cover` remplit le
	 * carré, plus lisible pour une photo, dont on veut juste reconnaître le sujet. */
	fit?: 'contain' | 'cover';
	onPick: () => void;
}) {
	const tokens = useDesignTokens();
	const [isHover, setIsHover] = React.useState(false);
	const active = isHover || !!props.highlighted;

	if (props.loading) {
		return (
			<View w={SIZE} h={SIZE} br={6} center bg={tokens.fill.secondary} style={{ flexShrink: 0 }}>
				<Spin size="small" />
			</View>
		);
	}

	if (!props.src) {
		return (
			<Tooltip title="Ajouter une image">
				<button
					type="button"
					aria-label="Ajouter une image"
					onClick={props.onPick}
					onMouseEnter={() => setIsHover(true)}
					onMouseLeave={() => setIsHover(false)}
					style={{
						width: SIZE,
						height: SIZE,
						flexShrink: 0,
						borderRadius: 6,
						border: `1px dashed ${active ? tokens.color.primary : tokens.border.default}`,
						background: active ? tokens.fill.tertiary : tokens.fill.secondary,
						color: active ? tokens.color.primary : tokens.text.muted,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						padding: 0,
						cursor: 'pointer',
						transition: 'border-color 0.15s, background 0.15s, color 0.15s',
					}}
				>
					<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
						<path d="M12 4v16M4 12h16" />
					</svg>
				</button>
			</Tooltip>
		);
	}

	return (
		<View w={SIZE} h={SIZE} rel style={{ flexShrink: 0 }}>
			<View
				w={SIZE}
				h={SIZE}
				br={6}
				center
				bg={tokens.fill.secondary}
				overflow="hidden"
				style={props.highlighted ? { outline: `1px dashed ${tokens.color.primary}` } : undefined}
			>
				{/* Adresse brute, sans CORS : voir `corsImageSrc`. */}
				<Image
					src={props.src}
					height={SIZE}
					width={props.fit === 'cover' ? SIZE : undefined}
					fit={props.fit}
					preview={false}
				/>
			</View>

			<button
				type="button"
				aria-label="Remplacer l'image"
				onClick={props.onPick}
				style={{
					position: 'absolute',
					right: -5,
					bottom: -5,
					width: 20,
					height: 20,
					borderRadius: '50%',
					border: `2px solid ${tokens.surface.card}`,
					background: tokens.color.primary,
					color: '#fff',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					padding: 0,
					cursor: 'pointer',
				}}
			>
				<svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.5">
					<path d="m16.5 3.5 4 4L7 21H3v-4Z" />
				</svg>
			</button>
		</View>
	);
}
